import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import IncidentOverview from './components/IncidentOverview';
import InteractiveMap from './components/InteractiveMap';
import UserComments from './components/UserComments';
import HistoricalData from './components/HistoricalData';
import RelatedOutages from './components/RelatedOutages';
import NotificationSubscription from './components/NotificationSubscription';

const ServiceOutageDetails = () => {
  const [searchParams] = useSearchParams();
  const incidentId = searchParams?.get('id') || 'default';
  
  const [incident, setIncident] = useState(null);
  const [comments, setComments] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  // Mock incident data
  const mockIncident = {
    id: incidentId,
    serviceName: "Verizon Wireless",
    serviceType: "Mobile Carrier",
    status: "investigating",
    totalReports: 2847,
    affectedRegions: 12,
    duration: "2h 34m",
    peakReports: "1,247/hr",
    lastUpdated: "2 minutes ago",
    description: `Users across multiple regions are experiencing intermittent connectivity issues with mobile data services. The issue appears to be affecting 4G and 5G networks primarily in urban areas. Our technical teams are actively investigating the root cause and working to restore full service as quickly as possible.\n\nAffected services include mobile internet, messaging, and voice calls in some areas. Wi-Fi calling may serve as a temporary workaround for voice communications.`
  };

  // Mock comments data
  const mockComments = [
    {
      id: 1,
      author: "Sarah Johnson",
      content: "Experiencing the same issue in downtown Chicago. Mobile data completely down since 2 PM. Wi-Fi calling is working though.",
      timestamp: new Date(Date.now() - 900000),
      upvotes: 23,
      downvotes: 2,
      isVerified: true,
      replies: [
        {
          id: 11,
          author: "Mike Chen",
          content: "Same here in the Loop area. Seems to be affecting the entire downtown corridor.",
          timestamp: new Date(Date.now() - 600000),
          upvotes: 8,
          downvotes: 0,
          isVerified: false
        }
      ]
    },
    {
      id: 2,
      author: "Tech Support Tom",
      content: "For those affected, try toggling airplane mode on/off or restarting your device. This has helped some users regain partial connectivity.",
      timestamp: new Date(Date.now() - 1200000),
      upvotes: 45,
      downvotes: 3,
      isVerified: true,
      replies: []
    },
    {
      id: 3,
      author: "Jennifer Martinez",
      content: "This is the third major outage this month. Really considering switching carriers at this point. The reliability has been terrible lately.",
      timestamp: new Date(Date.now() - 1800000),
      upvotes: 67,
      downvotes: 12,
      isVerified: false,
      replies: []
    }
  ];

  // Mock historical data
  const mockHistoricalIncidents = [
    {
      id: 1,
      date: "Oct 25, 2024",
      time: "14:30 EST",
      severity: "major",
      duration: "3h 45m",
      reports: 1892,
      affectedAreas: "Northeast, Midwest",
      status: "resolved"
    },
    {
      id: 2,
      date: "Oct 18, 2024",
      time: "09:15 EST",
      severity: "minor",
      duration: "1h 20m",
      reports: 456,
      affectedAreas: "West Coast",
      status: "resolved"
    },
    {
      id: 3,
      date: "Oct 12, 2024",
      time: "16:45 EST",
      severity: "critical",
      duration: "5h 12m",
      reports: 3247,
      affectedAreas: "Nationwide",
      status: "resolved"
    }
  ];

  const mockTrendData = {
    frequency: [
      { date: "Oct 1", incidents: 2 },
      { date: "Oct 8", incidents: 1 },
      { date: "Oct 15", incidents: 3 },
      { date: "Oct 22", incidents: 2 },
      { date: "Oct 29", incidents: 1 }
    ],
    severity: [
      { severity: "Minor", count: 8 },
      { severity: "Major", count: 3 },
      { severity: "Critical", count: 1 }
    ]
  };

  // Mock related outages
  const mockRelatedOutages = [
    {
      id: "att-001",
      serviceName: "AT&T Wireless",
      serviceType: "Mobile Carrier",
      description: "Intermittent voice call issues reported in several metropolitan areas",
      reports: 1234,
      regions: 8,
      duration: "1h 45m",
      status: "investigating",
      lastUpdated: "15 minutes ago"
    },
    {
      id: "tmobile-002",
      serviceName: "T-Mobile",
      serviceType: "Mobile Carrier",
      description: "5G network performance degradation in urban areas",
      reports: 892,
      regions: 5,
      duration: "45m",
      status: "resolved",
      lastUpdated: "1 hour ago"
    }
  ];

  // Mock service provider data
  const mockServiceProvider = {
    name: "Verizon Communications",
    description: "Leading telecommunications company providing wireless, internet, and TV services across the United States.",
    statusPage: "https://www.verizon.com/support/residential/account/manage-account/service-outage-info",
    supportUrl: "https://www.verizon.com/support/",
    twitter: "VerizonSupport",
    reliability: 98.7,
    avgResolution: "2.3h",
    totalIncidents: 24
  };

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIncident(mockIncident);
      setComments(mockComments);
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [incidentId]);

  const handleAddComment = (commentData) => {
    const newComment = {
      id: Date.now(),
      author: "Current User",
      content: commentData?.text,
      timestamp: new Date(),
      upvotes: 0,
      downvotes: 0,
      isVerified: false,
      replies: []
    };

    if (commentData?.parentId) {
      setComments(prev => prev?.map(comment => 
        comment?.id === commentData?.parentId
          ? { ...comment, replies: [...comment?.replies, newComment] }
          : comment
      ));
    } else {
      setComments(prev => [newComment, ...prev]);
    }
  };

  const handleVoteComment = (commentId, voteType) => {
    setComments(prev => prev?.map(comment => {
      if (comment?.id === commentId) {
        return {
          ...comment,
          upvotes: voteType === 'up' ? comment?.upvotes + 1 : comment?.upvotes,
          downvotes: voteType === 'down' ? comment?.downvotes + 1 : comment?.downvotes
        };
      }
      return comment;
    }));
  };

  const handleLocationFilter = (regionId) => {
    console.log('Filtering by region:', regionId);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'map', label: 'Geographic Map', icon: 'Map' },
    { id: 'comments', label: 'Community', icon: 'MessageSquare' },
    { id: 'history', label: 'Historical Data', icon: 'Clock' },
    { id: 'related', label: 'Related Outages', icon: 'Link' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center gap-3">
                <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                <span className="text-muted-foreground">Loading incident details...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/outage-dashboard" className="hover:text-foreground transition-colors">
              Dashboard
            </Link>
            <Icon name="ChevronRight" size={14} />
            <span className="text-foreground">Service Outage Details</span>
          </nav>

          {/* Main Content */}
          <div className="space-y-8">
            {/* Incident Overview - Always Visible */}
            <IncidentOverview incident={incident} />

            {/* Tab Navigation */}
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    {tab?.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <InteractiveMap 
                      outageData={{}} 
                      onLocationFilter={handleLocationFilter} 
                    />
                  </div>
                )}

                {activeTab === 'map' && (
                  <InteractiveMap 
                    outageData={{}} 
                    onLocationFilter={handleLocationFilter} 
                  />
                )}

                {activeTab === 'comments' && (
                  <UserComments
                    comments={comments}
                    onAddComment={handleAddComment}
                    onVoteComment={handleVoteComment}
                  />
                )}

                {activeTab === 'history' && (
                  <HistoricalData
                    historicalIncidents={mockHistoricalIncidents}
                    trendData={mockTrendData}
                  />
                )}

                {activeTab === 'related' && (
                  <RelatedOutages
                    relatedOutages={mockRelatedOutages}
                    serviceProvider={mockServiceProvider}
                  />
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <NotificationSubscription
                  incidentId={incident?.id}
                  serviceName={incident?.serviceName}
                />

                {activeTab !== 'related' && (
                  <RelatedOutages
                    relatedOutages={mockRelatedOutages}
                    serviceProvider={mockServiceProvider}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceOutageDetails;